import { useEffect, useRef } from "react";
import { motionAllowed } from "../../lib/motion.js";
import { PixelField } from "./field.js";
import { readPalette, type ArtVariant } from "./palette.js";
import type { Outline } from "./picture.js";
import "./pixel-art.css";
import { SCENES, type SceneName } from "./scenes.js";

/**
 * A field of pixel art (DESIGN.md §9, "Pixel art"): one of four palettes, one
 * of the named scenes, and — where it is `interactive` — cells that lift away
 * from the pointer and settle.
 *
 * Decoration, so it is hidden from assistive technology and carries nothing a
 * reader needs. With reduced motion it is a still picture. Where the browser
 * has no canvas it is the palette's background colour, which is all a
 * decoration owes anyone.
 */
export function PixelArt({
  variant,
  scene,
  columns = 26,
  cell,
  interactive = false,
  outline,
  className,
}: {
  readonly variant: ArtVariant;
  /** Changing it turns the picture over, cell by cell. */
  readonly scene: SceneName;
  /** How many cells across: fewer is chunkier. */
  readonly columns?: number;
  /** A fixed cell size in pixels, for art whose box changes width: wins over `columns`. */
  readonly cell?: number;
  readonly interactive?: boolean;
  /**
   * Bare: no background, the light at the outline's anchor, and nothing past
   * its edge, so the picture dissolves into the surface it sits on. For a
   * drop in a surface's empty room rather than a panel of its own. A module
   * constant, not one made each render, or the art is set up again.
   */
  readonly outline?: Outline;
  readonly className?: string;
}) {
  const host = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const field = useRef<PixelField | null>(null);
  const shown = useRef({ variant, scene });

  // The canvas is drawn imperatively; this wires it up once.
  useEffect(() => {
    const box = host.current;
    const surface = canvas.current;
    const ctx = surface?.getContext("2d") ?? null;
    if (box === null || surface === null || ctx === null) return;
    const moves = motionAllowed();
    const art = new PixelField(surface, ctx, readPalette(box, shown.current.variant), {
      columns,
      cell: cell ?? null,
      still: !moves,
      outline: outline ?? null,
    });
    field.current = art;
    const fit = () => {
      // Layout size, not the painted box: a modal arriving at 0.965 scale
      // would otherwise size the canvas short and leave a strip unpainted.
      const width = box.clientWidth;
      const height = box.clientHeight;
      if (width === 0 || height === 0) return;
      art.resize(width, height);
      art.show(SCENES[shown.current.scene]);
    };
    const sizes = new ResizeObserver(fit);
    sizes.observe(box);

    let previous: { x: number; y: number } | null = null;
    const move = (event: PointerEvent) => {
      const point = { x: event.offsetX, y: event.offsetY };
      if (previous !== null) {
        // Sampled along the path, so a fast flick lifts a trail, not two dots.
        const dx = point.x - previous.x;
        const dy = point.y - previous.y;
        const distance = Math.hypot(dx, dy);
        const steps = Math.max(1, Math.ceil(distance / (art.cell * 0.8)));
        for (let k = 1; k <= steps; k++) {
          art.touch(
            previous.x + (dx * k) / steps,
            previous.y + (dy * k) / steps,
            Math.min(1, 0.45 + distance / 26),
          );
        }
      }
      previous = point;
    };
    const leave = () => {
      previous = null;
    };
    if (interactive && moves) {
      box.addEventListener("pointermove", move);
      box.addEventListener("pointerleave", leave);
    }
    return () => {
      sizes.disconnect();
      box.removeEventListener("pointermove", move);
      box.removeEventListener("pointerleave", leave);
      art.stop();
      field.current = null;
    };
  }, [columns, cell, interactive, outline]);

  // A new scene or palette turns the picture over rather than replacing it.
  useEffect(() => {
    const box = host.current;
    if (shown.current.variant === variant && shown.current.scene === scene) return;
    shown.current = { variant, scene };
    if (box !== null) field.current?.show(SCENES[scene], readPalette(box, variant));
  }, [variant, scene]);

  return (
    <div
      ref={host}
      className={className === undefined ? "pixel-art" : `pixel-art ${className}`}
      data-variant={variant}
      data-interactive={interactive ? "" : undefined}
      data-bare={outline === undefined ? undefined : ""}
      aria-hidden="true"
    >
      <canvas ref={canvas} className="pixel-art-canvas" />
    </div>
  );
}
